import React, { useState } from "react";
import { Field } from "redux-form";

import {
  Box,
  Button,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { ArrowLeftIcon } from "@braintrust/braintrust-ui-components/Icons";
import {
  useBeginPasskeyLoginMutation,
  useCompletePasskeyLoginMutation,
} from "@js/apps/auth/api";
import type { LogInError } from "@js/apps/auth/types";
import { MFAMethodSelection } from "@js/apps/common/forms/fields/otp/mfa-method-selection";
import { OTPAuthCodeField } from "@js/apps/common/forms/fields/otp/otp-auth-code";
import {
  useWebAuthnAssertion,
  WebAuthnAssertionPrompt,
} from "@js/apps/common/forms/fields/otp/webauthn-assertion";
import { useEffectOnce } from "@js/apps/common/hooks/use-effect-once";
import { Modal } from "@js/components/modal";
import { CheckboxField } from "@js/forms/fields/checkbox";

import styles from "./styles.module.scss";

export const LoginMFAModal = Modal("LoginMFAModal", {
  closeButton: false,
  className: styles.modal,
  keepOnBackdropClick: true,
});

const MODE = {
  SELECT: "select",
  OTP: "otp",
  PASSKEY: "passkey",
} as const;

type Mode = ObjectValues<typeof MODE>;

type LoginMFAModalBodyProps = {
  errors: LogInError;
  onPasskeySuccess: () => Promise<void>;
};

const LoginMFAModalBody = ({
  errors,
  onPasskeySuccess,
}: LoginMFAModalBodyProps) => {
  const isEmailOTP = errors.otp_type?.[0] === ENUMS.OTPVerificationMethod.EMAIL;

  if (isEmailOTP) {
    return <EmailOTPModalContent />;
  }

  const otpAvailable = errors.otp_available?.[0] === "True";
  const passkeyAvailable = errors.passkey_available?.[0] === "True";

  return (
    <LoginMFAContent
      otpAvailable={otpAvailable}
      passkeyAvailable={passkeyAvailable}
      onPasskeySuccess={onPasskeySuccess}
    />
  );
};

type LoginMFAContentProps = {
  otpAvailable: boolean;
  passkeyAvailable: boolean;
  onPasskeySuccess: () => Promise<void>;
};

const LoginMFAContent = ({
  otpAvailable,
  passkeyAvailable,
  onPasskeySuccess,
}: LoginMFAContentProps) => {
  const bothAvailable = otpAvailable && passkeyAvailable;

  const [mode, setMode] = useState<Mode>(() => {
    if (bothAvailable) {
      return MODE.SELECT;
    }
    return otpAvailable ? MODE.OTP : MODE.PASSKEY;
  });

  if (mode === MODE.SELECT) {
    return (
      <MFAMethodSelection
        onSelectOtp={() => setMode(MODE.OTP)}
        onSelectPasskey={() => setMode(MODE.PASSKEY)}
        onCancel={LoginMFAModal.close}
      />
    );
  }

  if (mode === MODE.PASSKEY) {
    return (
      <PasskeyOTPModalContent
        hasMoreMethods={bothAvailable}
        onSelectOtherMethod={() => setMode(MODE.SELECT)}
        onSuccess={onPasskeySuccess}
      />
    );
  }

  return (
    <LoginAuthAppContent
      hasMoreMethods={bothAvailable}
      onSelectOtherMethod={() => setMode(MODE.SELECT)}
    />
  );
};

type LoginAuthAppContentProps = {
  hasMoreMethods: boolean;
  onSelectOtherMethod: () => void;
};

const LoginAuthAppContent = ({
  hasMoreMethods,
  onSelectOtherMethod,
}: LoginAuthAppContentProps) => {
  return (
    <Box>
      <Typography
        component="h4"
        variant="title"
        fontWeight={400}
        sx={{ mb: 2 }}
      >
        2-step authentication
      </Typography>
      <Typography component="p" sx={{ mb: 4 }}>
        Enter the 6-digit code generated by your authenticator app.
      </Typography>
      <OTPAuthCodeField
        inputProps={{
          "aria-describedby": "otp-description",
          autoFocus: true,
        }}
      />
      <Field
        name="is_backup_code"
        component={CheckboxField}
        label="Use backup code instead"
      />
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", mt: "45px" }}
      >
        <Button
          variant="transparent"
          shape="squared"
          startIcon={<ArrowLeftIcon />}
          sx={{ ml: -1, color: "var(--grey-2) !important" }}
          size="x-small"
          onClick={hasMoreMethods ? onSelectOtherMethod : LoginMFAModal.close}
        >
          {hasMoreMethods ? "Use different method" : "Cancel"}
        </Button>
        <Button variant="dark-violet" shape="squared" type="submit">
          Verify
        </Button>
      </Stack>
    </Box>
  );
};

type PasskeyOTPModalContentProps = {
  hasMoreMethods: boolean;
  onSelectOtherMethod: () => void;
  onSuccess: () => Promise<void>;
};

const PasskeyOTPModalContent = ({
  hasMoreMethods,
  onSelectOtherMethod,
  onSuccess,
}: PasskeyOTPModalContentProps) => {
  const [beginPasskeyLogin] = useBeginPasskeyLoginMutation();
  const [completePasskeyLogin] = useCompletePasskeyLoginMutation();
  const { authenticate, isAuthenticating, error } = useWebAuthnAssertion({
    begin: () => beginPasskeyLogin().unwrap(),
    complete: (assertion) => completePasskeyLogin(assertion).unwrap(),
  });

  const handleAuthenticate = async () => {
    const result = await authenticate();
    if (result !== undefined) {
      await onSuccess();
    }
  };

  useEffectOnce(() => {
    if (hasMoreMethods) {
      void handleAuthenticate();
    }
  });

  return (
    <WebAuthnAssertionPrompt
      error={error}
      isAuthenticating={isAuthenticating}
      onAuthenticate={handleAuthenticate}
      onCancel={hasMoreMethods ? onSelectOtherMethod : LoginMFAModal.close}
      cancelLabel={hasMoreMethods ? "Use different method" : "Cancel"}
    />
  );
};

const EMAIL_OTP_DESCRIPTION_ID = "email-otp-description";

const EmailOTPModalContent = () => {
  return (
    <Box>
      <Typography
        component="h4"
        variant="title"
        fontWeight={400}
        sx={{ mb: 2 }}
      >
        Email verification
      </Typography>
      <Typography component="p" sx={{ mb: 4 }}>
        Enter the 6-digit code sent to your email address.
      </Typography>
      <OTPAuthCodeField
        inputProps={{
          "aria-describedby": EMAIL_OTP_DESCRIPTION_ID,
          autoFocus: true,
        }}
      />

      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", mt: "45px" }}
      >
        <Button
          variant="transparent"
          shape="squared"
          startIcon={<ArrowLeftIcon />}
          sx={{ ml: -1, color: "var(--grey-2) !important" }}
          size="x-small"
          onClick={LoginMFAModal.close}
        >
          Cancel
        </Button>
        <Button variant="dark-violet" shape="squared" type="submit">
          Verify
        </Button>
      </Stack>
    </Box>
  );
};

export const openLoginMFAModal = (props: LoginMFAModalBodyProps) => {
  LoginMFAModal.open({
    children: <LoginMFAModalBody {...props} />,
  });
};

export const closeLoginMFAModal = () => LoginMFAModal.close();
